import { reactive, computed, ref, Ref } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

export enum CustomColumnTypeEnum {
  TEXT = "Text",
  NUMBER = "Number",
  DATE = "Date",
  DOLLAR = "Dollar",
}

export interface CustomColumnDto {
  type: CustomColumnTypeEnum;
  name: string;
  value?: string | number | Date;
  order?: number;
}

export interface ColumnOrderDto {
  displayName: string;
  position: number;
  type: "default" | "custom";
  visible: boolean;
  customType?: CustomColumnTypeEnum;
}

export interface ColumnConfig {
  value: string;
  isSelected: boolean;
  required: boolean;
  order?: number;
  isCustom?: boolean;
  customType?: CustomColumnTypeEnum;
}

export interface ExtraColumns {
  deposit: ColumnConfig;
  lotBuyers: ColumnConfig;
  exchanged: ColumnConfig;
  financeDue: ColumnConfig;
  settlement: ColumnConfig;
  [key: string]: ColumnConfig;
}

export interface UpdateEvent {
  type: string;
  name?: string;
  draggedColumn?: string;
  targetColumn?: string;
  customColumn?: CustomColumnDto;
  idx?: number;
}

export function useLotsColumns(stageId?: string) {
  const store = useStore();
  const toast = useToast();
  const isUpdating = ref(false);

  const getDefaultColumns = (): ExtraColumns => ({
    deposit: {
      value: "Deposit",
      isSelected: true,
      required: false,
      order: 1,
    },
    lotBuyers: {
      value: "Buyer(s)",
      isSelected: true,
      required: false,
      order: 2,
    },
    registrationDate: {
      value: "Registration Date",
      isSelected: true,
      required: false,
      order: 3,
    },
    exchanged: {
      value: "Signed",
      isSelected: true,
      required: false,
      order: 4,
    },
    financeDue: {
      value: "Finance due",
      isSelected: true,
      required: false,
      order: 5,
    },
    settlement: {
      value: "Unconditional",
      isSelected: true,
      required: false,
      order: 6,
    },
    conditions: {
      value: "Conditions",
      isSelected: true,
      required: false,
      order: 7,
    },
  });

  const extraColumns = reactive<ExtraColumns>(getDefaultColumns());
  const customColumns = ref<CustomColumnDto[]>([]);

  const columnsArray = computed(() => {
    const extraColumnsArray = Object.entries(extraColumns).map(
      ([key, col]) => ({
        key,
        value: col.value,
        isSelected: col.isSelected,
        required: col.required,
        order: col.order || 999,
        isCustom: false,
      })
    );

    const customColumnsArray = customColumns.value.map((col) => ({
      key: `custom_${col.name}`,
      value: col.name,
      isSelected: true,
      required: false,
      order: col.order || 999,
      isCustom: true,
      customType: col.type,
    }));

    return [...extraColumnsArray, ...customColumnsArray].sort(
      (a, b) => a.order - b.order
    );
  });

  const pinnedLotColumns = computed(
    () =>
      store.state.user.data.detailsId.pageSettings?.lotsTab?.showColumns || []
  );

  const handleColumnUpdate = async (update: UpdateEvent) => {
    isUpdating.value = true;

    try {
      if (
        update.type === "reorder" &&
        update.draggedColumn &&
        update.targetColumn
      ) {
        const currentColumns = columnsArray.value.filter(
          (col) => col.isSelected
        );

        const draggedIndex = currentColumns.findIndex(
          (col) => col.value === update.draggedColumn
        );
        const targetIndex = currentColumns.findIndex(
          (col) => col.value === update.targetColumn
        );

        if (draggedIndex === -1 || targetIndex === -1) return;

        const draggedCol = currentColumns.splice(draggedIndex, 1)[0];

        currentColumns.splice(targetIndex, 0, draggedCol);

        currentColumns.forEach((col, index) => {
          const newOrder = index + 1;

          if (col.isCustom) {
            const customCol = customColumns.value.find(
              (c) => c.name === col.value
            );

            if (customCol) {
              customCol.order = newOrder;
            }
          } else {
            const extraKey = Object.keys(extraColumns).find(
              (key) => extraColumns[key].value === col.value
            );

            if (extraKey) {
              extraColumns[extraKey].order = newOrder;
            }
          }
        });
        await saveColumnsOrder();
        await saveCustomColumns();
      } else if (update.type === "reset") {
        await resetToDefault();
      } else if (update.type === "addCustom" && update.customColumn) {
        await addCustomColumn(update.customColumn);
      } else if (update.type === "removeCustom" && update.idx !== undefined) {
        await removeCustomColumn(update.idx);
      } else if (update.name) {
        const column = Object.keys(extraColumns).find(
          (key) => extraColumns[key].value === update.name
        );

        if (column) {
          extraColumns[column].isSelected = update.type === "add";
          await saveColumnsOrder();
          saveSelectedLotExtraColumns();
        }
      }
    } catch (error) {
      console.error("Error updating columns:", error);
      toast.error("Failed to update columns. Please try again.");
    } finally {
      isUpdating.value = false;
    }
  };

  const saveSelectedLotExtraColumns = () => {
    const lotColumns = [];

    for (const item in extraColumns) {
      if (extraColumns[item].isSelected) {
        lotColumns.push(extraColumns[item].value);
      }
    }

    store.dispatch("user/saveSelectedLotExtraColumns", lotColumns);
  };

  const saveColumnsOrder = async () => {
    if (!stageId) {
      console.warn(
        "No stageId provided, cannot save columns order to database"
      );
      return;
    }

    const columnOrderData: ColumnOrderDto[] = [];

    for (const [key, col] of Object.entries(extraColumns)) {
      columnOrderData.push({
        displayName: col.value,
        position: col.order || 999,
        type: "default",
        visible: col.isSelected,
      });
    }

    customColumns.value.forEach((col) => {
      columnOrderData.push({
        displayName: col.name,
        position: col.order || 999,
        type: "custom",
        visible: true,
        customType: col.type,
      });
    });

    try {
      await store.dispatch("stage/updateStageColumnOrder", {
        stageId,
        columnOrderData,
      });
    } catch (error) {
      console.error("Failed to save columns order to database:", error);
      toast.error("Failed to save column order. Please try again.");
      throw error;
    }
  };

  const loadColumnsOrder = async () => {
    if (!stageId) {
      console.warn(
        "No stageId provided, cannot load columns order from database"
      );
      return;
    }

    try {
      const response = await store.dispatch(
        "stage/getStageColumnOrder",
        stageId
      );
      const columnOrderData: ColumnOrderDto[] = response.data || [];
      const defaultColumns = getDefaultColumns();

      Object.assign(extraColumns, defaultColumns);

      columnOrderData.forEach((columnOrder) => {
        if (columnOrder.type === "default") {
          const extraKey = Object.keys(extraColumns).find(
            (key) => extraColumns[key].value === columnOrder.displayName
          );

          if (extraKey) {
            extraColumns[extraKey].order = columnOrder.position;
            extraColumns[extraKey].isSelected = columnOrder.visible;
          }
        } else if (columnOrder.type === "custom") {
          const existingCustomColumn = customColumns.value.find(
            (col) => col.name === columnOrder.displayName
          );

          if (existingCustomColumn) {
            existingCustomColumn.order = columnOrder.position;
          }
        }
      });
    } catch (error) {
      console.warn("Failed to load columns order from database:", error);
      const defaultColumns = getDefaultColumns();

      Object.assign(extraColumns, defaultColumns);
    }
  };

  const showAllColumns = (isSelected: boolean) => {
    for (const item in extraColumns) {
      extraColumns[item].isSelected = isSelected;
    }

    saveSelectedLotExtraColumns();
  };

  const resetToDefault = async () => {
    const defaultColumns = getDefaultColumns();

    Object.keys(extraColumns).forEach((key) => {
      if (defaultColumns[key]) {
        extraColumns[key].order = defaultColumns[key].order;
        extraColumns[key].isSelected = defaultColumns[key].isSelected;
      }
    });

    customColumns.value = [];

    await saveColumnsOrder();
    saveSelectedLotExtraColumns();
    toast.success("Columns reset to default");
  };

  const addCustomColumn = async (column: CustomColumnDto) => {
    const exists = customColumns.value.some((col) => col.name === column.name);

    if (exists) {
      console.warn(`Custom column '${column.name}' already exists`);
      return;
    }

    customColumns.value.push({
      type: column.type,
      name: column.name,
      value: column.value,
      order:
        column.order ||
        Math.max(0, ...columnsArray.value.map((c) => c.order || 0)) + 1,
    });

    await saveCustomColumns();
    toast.success(`Custom column "${column.name}" added`);
  };

  const removeCustomColumn = async (index: number) => {
    if (index >= 0 && index < customColumns.value.length) {
      const columnName = customColumns.value[index].name;

      customColumns.value.splice(index, 1);
      await saveCustomColumns();
      toast.success(`Custom column "${columnName}" removed`);
    }
  };

  const saveCustomColumns = async () => {
    await saveColumnsOrder();
  };

  const loadCustomColumns = async () => {
    if (!stageId) {
      console.warn(
        "No stageId provided, cannot load custom columns from database"
      );
      return;
    }

    try {
      const response = await store.dispatch(
        "stage/getStageColumnOrder",
        stageId
      );
      const columnOrderData: ColumnOrderDto[] = response.data || [];

      customColumns.value = [];

      columnOrderData.forEach((columnOrder) => {
        if (columnOrder.type === "custom") {
          customColumns.value.push({
            type: columnOrder.customType || CustomColumnTypeEnum.TEXT,
            name: columnOrder.displayName,
            order: columnOrder.position,
          });
        }
      });
    } catch (error) {
      console.warn("Failed to load custom columns from database:", error);
    }
  };

  const initializeColumns = async () => {
    await loadColumnsOrder();
    await loadCustomColumns();

    if (!stageId) {
      pinnedLotColumns.value.forEach((column: string) => {
        const objKey = Object.keys(extraColumns).find(
          (key) => extraColumns[key].value === column
        );

        if (objKey) {
          extraColumns[objKey].isSelected = true;
        }
      });
    }
  };

  return {
    extraColumns,
    customColumns,
    columnsArray,
    pinnedLotColumns,
    handleColumnUpdate,
    saveSelectedLotExtraColumns,
    saveColumnsOrder,
    loadColumnsOrder,
    showAllColumns,
    resetToDefault,
    addCustomColumn,
    removeCustomColumn,
    saveCustomColumns,
    loadCustomColumns,
    initializeColumns,
    isUpdating,
  };
}
